import { datadogLogs } from '@datadog/browser-logs'
import { FirebaseError } from 'firebase/app'
import {
  onAuthStateChanged as _onAuthStateChanged,
  onIdTokenChanged as _onIdTokenChanged,
  NextOrObserver,
  User,
  UserCredential,
  signInWithCustomToken,
  signOut as _signOut,
} from 'firebase/auth'

import { auth } from './client-app'

export type FirebaseUser = User

export const onAuthStateChanged = (callback: NextOrObserver<FirebaseUser>) => {
  return _onAuthStateChanged(auth, callback)
}

export const onIdTokenChanged = (callback: NextOrObserver<FirebaseUser>) => {
  return _onIdTokenChanged(auth, callback)
}

const NON_RETRYABLE_ERRORS = new Set([
  'auth/invalid-custom-token',
  'auth/custom-token-mismatch',
  'auth/user-disabled',
  'auth/app-not-authorized',
])

const getFirebaseErrorCode = (error: unknown): string => {
  if (error instanceof FirebaseError) return error.code
  if (error instanceof Error) return error.name

  return 'unknown'
}

const isRetryable = (error: unknown): boolean => {
  if (!(error instanceof FirebaseError)) return true

  return !NON_RETRYABLE_ERRORS.has(error.code)
}

// createLogger는 init 전에 호출해도 안전 (SDK가 큐잉 처리)
const firebaseAuthLogger = datadogLogs.createLogger('firebase_auth')

const logFirebaseAuth = (
  level: 'info' | 'warn' | 'error',
  message: string,
  context?: Record<string, unknown>,
) => {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT === 'live') {
    firebaseAuthLogger[level](message, context)
  } else {
    console[level]('[firebase_auth]', message, context)
  }
}

export const signInWithToken = async (
  customToken: string,
): Promise<UserCredential> => {
  const MAX_RETRIES = 2

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const credential = await signInWithCustomToken(auth, customToken)

      if (attempt > 0) {
        logFirebaseAuth('info', 'Firebase sign-in succeeded after retry', {
          attempt,
        })
      }

      return credential
    } catch (error) {
      const errorCode = getFirebaseErrorCode(error)

      if (!isRetryable(error) || attempt === MAX_RETRIES) {
        logFirebaseAuth('error', 'Firebase sign-in failed', {
          errorCode,
          attempts: attempt + 1,
        })
        throw error
      }

      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
    }
  }

  throw new Error('Firebase sign-in: unreachable')
}

export const signOut = async () => {
  try {
    await _signOut(auth)
  } catch (error) {
    logFirebaseAuth('error', 'Firebase sign-out failed', {
      errorCode: getFirebaseErrorCode(error),
    })
  }
}
